function getAlignedRowHeights(productRows) {
  const rowCount = productRows.reduce((maximum, rows) => Math.max(maximum, rows.length), 0);

  return Array.from({ length: rowCount }, (_, rowIndex) =>
    productRows.reduce((maximum, rows) => Math.max(maximum, Number(rows[rowIndex]) || 0), 0)
  );
}

function getCanonicalRowLayout(productRows) {
  const canonicalRows = productRows[0] || [];
  const canonicalRowCount = canonicalRows.length;
  const comparableRows = productRows.map((rows) => rows.slice(0, canonicalRowCount).map((row) => row.height));
  const rowHeights = getAlignedRowHeights(comparableRows).slice(0, canonicalRowCount);

  return {
    canonicalRowCount,
    groupHeadings: canonicalRows.map((row) => Boolean(row.groupHeading)),
    rowHeights,
    totalHeight: rowHeights.reduce((total, height) => total + height, 0),
  };
}

function applyCanonicalRowMetadata(productRows) {
  const canonicalRows = productRows[0] || [];
  const canonicalMetadata = canonicalRows.map((row) => ({
    groupHeading: row.classList.contains('spec-comparison__item--group-heading'),
    label: row.getAttribute('aria-label') || '',
  }));

  productRows.forEach((rows) => {
    rows.forEach((row, rowIndex) => {
      const metadata = canonicalMetadata[rowIndex];
      row.hidden = metadata === undefined;

      if (metadata) {
        row.classList.toggle('spec-comparison__item--group-heading', metadata.groupHeading);
        row.setAttribute('aria-label', metadata.label);
      }
    });
  });

  return canonicalMetadata;
}

class SpecComparison extends (typeof HTMLElement === 'undefined' ? class {} : HTMLElement) {
  connectedCallback() {
    this.productsContainer = this.querySelector('[data-spec-comparison-products]');
    this.resizeObserver = new ResizeObserver(() => this.queueSync());
    this.resizeObserver.observe(this);
    this.queueSync();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.syncFrame);
  }

  queueSync() {
    cancelAnimationFrame(this.syncFrame);
    this.syncFrame = requestAnimationFrame(() => this.syncLayout());
  }

  syncLayout() {
    const products = Array.from(this.querySelectorAll('[data-spec-comparison-product]'));
    if (!this.productsContainer) return;

    this.productsContainer.hidden = products.length === 0;
    if (products.length === 0) {
      this.productsContainer.style.removeProperty('--spec-products-min-width');
      return;
    }

    this.productsContainer.style.setProperty('--spec-products-min-width', `${products.length * 180}px`);
    this.syncElements(products, '[data-spec-comparison-header]');
    this.syncRows(products);
    this.syncElements(products, '[data-spec-comparison-price]');
    this.syncElements(products, '[data-spec-comparison-button]');
  }

  syncElements(products, selector) {
    const elements = products.map((product) => product.querySelector(selector)).filter(Boolean);
    if (elements.length === 0) return;

    elements.forEach((element) => element.style.removeProperty('min-height'));
    const height = Math.max(...elements.map((element) => element.getBoundingClientRect().height));
    elements.forEach((element) => element.style.minHeight = `${height}px`);
  }

  syncRows(products) {
    const productRows = products.map((product) =>
      Array.from(product.querySelectorAll('[data-spec-comparison-item]'))
    );
    applyCanonicalRowMetadata(productRows);

    productRows.forEach((rows) => {
      rows.forEach((row) => {
        row.style.removeProperty('min-height');
      });
    });

    const layout = getCanonicalRowLayout(
      productRows.map((rows) =>
        rows.map((row) => ({
          height: row.hidden ? 0 : row.getBoundingClientRect().height,
          groupHeading: row.classList.contains('spec-comparison__item--group-heading'),
        }))
      )
    );

    productRows.forEach((rows) => {
      rows.slice(0, layout.canonicalRowCount).forEach((row, rowIndex) => {
        row.style.minHeight = `${layout.rowHeights[rowIndex]}px`;
      });
    });

    products.forEach((product) => {
      const items = product.querySelector('.spec-comparison__product-items');
      if (items) items.style.minHeight = `${layout.totalHeight}px`;
    });
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('spec-comparison')) {
  customElements.define('spec-comparison', SpecComparison);
}

if (typeof module !== 'undefined') {
  module.exports = { applyCanonicalRowMetadata, getAlignedRowHeights, getCanonicalRowLayout };
}
